import { useEffect, useState, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import { StaffCombobox } from "@/components/StaffCombobox";
import { NeedsAttention } from "@/components/NeedsAttention";
import { TicketDigestDialog } from "@/components/TicketDigestDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { LifeBuoy, Plus, Search, Paperclip, Trash2, Settings, Download, UserCheck, X, MessageSquare, Clock, Send, Gauge, FileText, CalendarDays, TreePalm } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { toast } from "sonner";
import { fetchAwayMap, awayFor, fmtBack } from "@/components/AwayBadge";
import { CreateTicketDialog } from "@/components/tickets/CreateTicketDialog";
import { TICKET_STATUS as STATUS, TICKET_STATUS_ORDER as STATUS_ORDER, TICKET_PRIORITY as PRIORITY, SLA_BADGE } from "@/constants/tickets";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const fmt = (t) => { try { return new Date(t).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };
const isOverdue = (t) => t.due_date && !["resolved", "closed"].includes(t.status) && new Date(t.due_date + "T00:00:00") < new Date(new Date().toDateString());

const Chip = ({ cls, children }) => <span className={`text-[11px] px-2 py-0.5 rounded-sm font-medium ${cls}`}>{children}</span>;

const SlaBadge = ({ sla }) => { const b = sla && SLA_BADGE[sla.state]; return b ? <Chip cls={b.cls}>{b.label}</Chip> : null; };
const slaCellCls = (s) => ({ breached: "bg-red-600/15 text-red-600", at_risk: "bg-amber-500/15 text-amber-600", late: "bg-red-600/10 text-red-600", met: "bg-emerald-500/15 text-emerald-600" }[s] || "bg-secondary text-muted-foreground");
const slaLabel = (part) => { if (!part) return ""; if (part.done) return part.state === "met" ? "met on time" : "missed target"; if (part.state === "breached") return `overdue since ${fmt(part.due)}`; return `by ${fmt(part.due)}`; };

function SlaDialog({ open, setOpen }) {
  const [cfg, setCfg] = useState(null);
  useEffect(() => { if (open) api.get("/tickets/sla").then((r) => setCfg(r.data)).catch(() => {}); }, [open]);
  const setVal = (p, k, hrs) => setCfg((c) => ({ ...c, [p]: { ...c[p], [k]: Math.max(1, Math.round(parseFloat(hrs || 0) * 60)) } }));
  const save = async () => { try { await api.put("/tickets/sla", cfg); toast.success("SLA targets saved"); setOpen(false); } catch { toast.error("Could not save"); } };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg" data-testid="sla-dialog">
        <DialogHeader><DialogTitle>SLA Targets</DialogTitle><DialogDescription>Response &amp; resolution goals per priority, in hours.</DialogDescription></DialogHeader>
        {cfg && (
          <div className="space-y-2">
            <div className="grid grid-cols-3 gap-3 text-[11px] uppercase tracking-wide text-muted-foreground px-1"><span>Priority</span><span>Respond (hrs)</span><span>Resolve (hrs)</span></div>
            {["urgent", "high", "medium", "low"].map((p) => (
              <div key={p} className="grid grid-cols-3 gap-3 items-center">
                <span className="text-sm capitalize flex items-center gap-1.5"><span className={`h-2 w-2 rounded-full ${PRIORITY[p].dot}`} />{p}</span>
                <Input type="number" step="0.5" min="0.25" data-testid={`sla-resp-${p}`} value={cfg[p].response / 60} onChange={(e) => setVal(p, "response", e.target.value)} className="h-8" />
                <Input type="number" step="1" min="1" data-testid={`sla-reso-${p}`} value={cfg[p].resolution / 60} onChange={(e) => setVal(p, "resolution", e.target.value)} className="h-8" />
              </div>
            ))}
            <div className="grid grid-cols-3 gap-3 items-center pt-3 mt-2 border-t border-border/60">
              <span className="text-sm col-span-2 leading-tight">Waiting reminder<span className="block text-[11px] text-muted-foreground">Nudge the assignee &amp; their manager after this many quiet days</span></span>
              <Input type="number" step="1" min="1" data-testid="sla-waiting-days" value={cfg.waiting_reminder_days ?? 2} onChange={(e) => setCfg((c) => ({ ...c, waiting_reminder_days: Math.max(1, parseInt(e.target.value || 1, 10)) }))} className="h-8" />
            </div>
            <div className="grid grid-cols-3 gap-3 items-center">
              <span className="text-sm col-span-2 leading-tight">Auto-resolve after reminder<span className="block text-[11px] text-muted-foreground">Days of continued silence before a Waiting ticket auto-resolves (0 = off)</span></span>
              <Input type="number" step="1" min="0" data-testid="sla-auto-resolve-days" value={cfg.auto_resolve_days ?? 3} onChange={(e) => setCfg((c) => ({ ...c, auto_resolve_days: Math.max(0, parseInt(e.target.value || 0, 10)) }))} className="h-8" />
            </div>
          </div>
        )}
        <DialogFooter><Button onClick={save} data-testid="sla-save">Save targets</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CannedPicker({ canManage, onInsert }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", body: "" });
  const load = useCallback(() => api.get("/canned-replies").then((r) => setItems(r.data)).catch(() => {}), []);
  useEffect(() => { if (open) load(); }, [open, load]);
  const add = async () => {
    if (!form.title.trim() || !form.body.trim()) return toast.error("Title & body required");
    try { await api.post("/canned-replies", form); setForm({ title: "", body: "" }); load(); } catch { toast.error("Could not save"); }
  };
  const del = async (id) => { try { await api.delete(`/canned-replies/${id}`); load(); } catch (e) { console.error("delete canned reply", e); toast.error("Could not delete reply"); } };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild><Button variant="outline" size="sm" data-testid="canned-btn"><FileText className="h-3.5 w-3.5 mr-1" />Templates</Button></PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="px-3 py-2 border-b border-border text-sm font-semibold">Canned replies</div>
        <div className="max-h-56 overflow-y-auto" data-testid="canned-list">
          {items.length === 0 ? <div className="p-3 text-xs text-muted-foreground">No templates yet.</div> :
            items.map((c) => (
              <div key={c.id} className="flex items-start gap-2 px-3 py-2 border-b border-border/50 hover:bg-accent">
                <button className="flex-1 text-left min-w-0" data-testid="canned-insert" onClick={() => { onInsert(c.body); setOpen(false); }}>
                  <div className="text-sm font-medium truncate">{c.title}</div>
                  <div className="text-xs text-muted-foreground line-clamp-2">{c.body}</div>
                </button>
                {canManage && <button onClick={() => del(c.id)} data-testid="canned-delete" className="text-muted-foreground hover:text-destructive shrink-0"><Trash2 className="h-3.5 w-3.5" /></button>}
              </div>
            ))}
        </div>
        {canManage && (
          <div className="p-3 border-t border-border space-y-2">
            <Input placeholder="Template title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="canned-title" className="h-8" />
            <Textarea rows={2} placeholder="Template text…" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="canned-body" />
            <Button size="sm" className="w-full" onClick={add} data-testid="canned-save"><Plus className="h-3.5 w-3.5 mr-1" />Save template</Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

function StatsStrip({ stats }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4" data-testid="ticket-stats">
      <div className="border border-border bg-card rounded-sm p-3"><div className="text-2xl font-condensed font-extrabold text-primary">{stats.open}</div><div className="text-[11px] uppercase tracking-wide text-muted-foreground">Open</div></div>
      <div className="border border-border bg-card rounded-sm p-3"><div className="text-2xl font-condensed font-extrabold">{stats.total}</div><div className="text-[11px] uppercase tracking-wide text-muted-foreground">Total</div></div>
      <div className="border border-border bg-card rounded-sm p-3"><div className="text-2xl font-condensed font-extrabold text-amber-600">{stats.by_status?.in_progress || 0}</div><div className="text-[11px] uppercase tracking-wide text-muted-foreground">In progress</div></div>
      <div className="border border-border bg-card rounded-sm p-3"><div className="text-2xl font-condensed font-extrabold text-emerald-600">{stats.avg_resolution_hours || 0}h</div><div className="text-[11px] uppercase tracking-wide text-muted-foreground">Avg resolution</div></div>
    </div>
  );
}

function ManageGroups({ open, setOpen, groups, reload }) {
  const [form, setForm] = useState({ name: "", department: "", description: "" });
  const add = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    try { await api.post("/support-groups", form); toast.success("Group created"); setForm({ name: "", department: "", description: "" }); reload(); }
    catch { toast.error("Could not create"); }
  };
  const addAgent = async (g, email) => {
    if (!email) return;
    const emails = [...(g.agent_emails || [])];
    if (!emails.includes(email.toLowerCase())) emails.push(email.toLowerCase());
    try { await api.put(`/support-groups/${g.id}`, { agent_emails: emails }); reload(); } catch { toast.error("Could not add agent"); }
  };
  const removeAgent = async (g, email) => {
    try { await api.put(`/support-groups/${g.id}`, { agent_emails: (g.agent_emails || []).filter((e) => e !== email) }); reload(); } catch (e) { console.error("remove agent", e); toast.error("Could not remove agent"); }
  };
  const del = async (g) => { try { await api.delete(`/support-groups/${g.id}`); reload(); toast.success("Deleted"); } catch (e) { console.error("delete group", e); toast.error("Could not delete group"); } };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-2xl" data-testid="manage-groups-dialog">
        <DialogHeader><DialogTitle>Support Groups</DialogTitle><DialogDescription>Create queues and assign the agents who work their tickets.</DialogDescription></DialogHeader>
        <div className="flex items-end gap-2 border border-border rounded-sm p-3">
          <div className="flex-1"><Label className="text-xs">New group</Label><Input data-testid="group-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. IT Support" /></div>
          <div className="flex-1"><Label className="text-xs">Department</Label><Input data-testid="group-dept" value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} placeholder="IT" /></div>
          <Button onClick={add} data-testid="group-add"><Plus className="h-4 w-4 mr-1" />Add</Button>
        </div>
        <div className="space-y-3 max-h-96 overflow-y-auto">
          {groups.map((g) => (
            <div key={g.id} className="border border-border rounded-sm p-3" data-testid="group-row">
              <div className="flex items-center justify-between">
                <div><span className="font-semibold text-sm">{g.name}</span>{g.department && <span className="text-xs text-muted-foreground"> · {g.department}</span>}</div>
                <button onClick={() => del(g)} className="text-muted-foreground hover:text-destructive" data-testid="group-delete"><Trash2 className="h-4 w-4" /></button>
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {(g.agent_emails || []).length === 0 && <span className="text-xs text-muted-foreground">No agents yet</span>}
                {(g.agent_emails || []).map((e) => (
                  <span key={e} className="text-xs inline-flex items-center gap-1 bg-secondary rounded-sm px-2 py-0.5">{e}<button onClick={() => removeAgent(g, e)} className="text-muted-foreground hover:text-destructive"><X className="h-3 w-3" /></button></span>
                ))}
              </div>
              <div className="mt-2 max-w-xs"><StaffCombobox testid={`group-agent-${g.id}`} value="" placeholder="Add agent…" onChange={(name, email) => addAgent(g, email)} /></div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TicketDetail({ ticket, groups, user, onClose, onChange }) {
  const t = ticket;
  const canAgent = t.can_agent;
  const [comment, setComment] = useState("");
  const [internal, setInternal] = useState(false);
  const [away, setAway] = useState(null);
  const fileRef = useRef(null);
  useEffect(() => {
    if (!t.assignee_email) { setAway(null); return; }
    fetchAwayMap().then((m) => setAway(awayFor(m, t.assignee_email) || null));
  }, [t.assignee_email]);
  const reroute = away?.backup_email && away.backup_email !== (t.assignee_email || "").toLowerCase() ? away : null;

  const patch = async (payload) => { try { await api.patch(`/tickets/${t.id}`, payload); onChange(); } catch { toast.error("Could not update"); } };
  const addComment = async () => {
    if (!comment.trim()) return;
    try { await api.post(`/tickets/${t.id}/comments`, { body: comment, internal }); setComment(""); setInternal(false); onChange(); }
    catch { toast.error("Could not post"); }
  };
  const upload = async (file) => { const fd = new FormData(); fd.append("file", file); try { await api.post(`/tickets/${t.id}/attachments`, fd); toast.success("Attached"); onChange(); } catch { toast.error("Upload failed"); } };

  return (
    <div className="border border-border bg-card rounded-sm p-5 animate-fade-up" data-testid="ticket-detail">
      <div className="flex items-start justify-between gap-3 pb-3 border-b border-border">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[11px] font-mono text-muted-foreground">{t.number}</span>
            <Chip cls={PRIORITY[t.priority]?.cls}>{PRIORITY[t.priority]?.label}</Chip>
            <Chip cls={STATUS[t.status]?.cls}>{STATUS[t.status]?.label}</Chip>
            <SlaBadge sla={t.sla} />
            {isOverdue(t) && <span className="text-[10px] text-destructive font-semibold">OVERDUE</span>}
          </div>
          <h2 className="font-display font-bold text-lg mt-1">{t.subject}</h2>
          <div className="text-xs text-muted-foreground mt-1">{t.group_name} · opened by {t.requester_name} · {fmt(t.created_at)}</div>
          {t.sla && (
            <div className="flex flex-wrap gap-2 mt-2" data-testid="ticket-sla">
              <span className={`text-[11px] px-2 py-0.5 rounded-sm ${slaCellCls(t.sla.response.state)}`}>Response {slaLabel(t.sla.response)}</span>
              <span className={`text-[11px] px-2 py-0.5 rounded-sm ${slaCellCls(t.sla.resolution.state)}`}>Resolution {slaLabel(t.sla.resolution)}</span>
            </div>
          )}
        </div>
        <button onClick={onClose} className="text-muted-foreground hover:text-foreground shrink-0" data-testid="ticket-close-detail"><X className="h-4 w-4" /></button>
      </div>

      {t.description && <p className="text-sm text-foreground/90 whitespace-pre-line mt-3">{t.description}</p>}

      <div className="mt-4">
        <div className="flex items-center justify-between">
          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Attachments</span>
          <input ref={fileRef} type="file" className="hidden" data-testid="ticket-attach-input" onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ""; }} />
          <button onClick={() => fileRef.current?.click()} className="text-xs text-primary hover:underline inline-flex items-center gap-1" data-testid="ticket-attach-btn"><Paperclip className="h-3 w-3" />Add file</button>
        </div>
        {t.attachments?.length ? (
          <div className="mt-2 space-y-1">
            {t.attachments.map((a) => (
              <button key={a.id} onClick={() => window.open(`${BACKEND}/api/tickets/${t.id}/attachments/${a.id}`, "_blank")} data-testid="ticket-attachment"
                className="w-full text-left text-xs flex items-center gap-2 border border-border rounded-sm px-2 py-1.5 hover:bg-accent transition-colors"><Paperclip className="h-3 w-3 text-muted-foreground" /><span className="truncate">{a.filename}</span></button>
            ))}
          </div>
        ) : <p className="text-xs text-muted-foreground mt-1">None</p>}
      </div>

      {canAgent ? (
        <div className="mt-4 grid grid-cols-2 gap-3 border-t border-border pt-4" data-testid="ticket-agent-controls">
          <div><Label className="text-xs">Status</Label><Select value={t.status} onValueChange={(v) => patch({ status: v })}><SelectTrigger data-testid="ticket-status-select"><SelectValue /></SelectTrigger><SelectContent>{STATUS_ORDER.map((s) => <SelectItem key={s} value={s}>{STATUS[s].label}</SelectItem>)}</SelectContent></Select></div>
          <div><Label className="text-xs">Priority</Label><Select value={t.priority} onValueChange={(v) => patch({ priority: v })}><SelectTrigger data-testid="ticket-priority-select"><SelectValue /></SelectTrigger><SelectContent>{Object.keys(PRIORITY).map((p) => <SelectItem key={p} value={p}>{PRIORITY[p].label}</SelectItem>)}</SelectContent></Select></div>
          <div><Label className="text-xs">Group</Label><Select value={t.group_id} onValueChange={(v) => patch({ group_id: v })}><SelectTrigger data-testid="ticket-group-select"><SelectValue /></SelectTrigger><SelectContent>{groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}</SelectContent></Select></div>
          <div><Label className="text-xs">Due date</Label><Input type="date" data-testid="ticket-due-select" value={t.due_date || ""} onChange={(e) => patch({ due_date: e.target.value })} /></div>
          <div className="col-span-2">
            <Label className="text-xs">Assignee</Label>
            <div className="flex gap-2 items-center">
              <div className="flex-1"><StaffCombobox testid="ticket-assignee" value={t.assignee_name} placeholder="Unassigned" onChange={(name, email) => patch({ assignee_name: name, assignee_email: email })} /></div>
              <Button size="sm" variant="outline" data-testid="ticket-assign-self" onClick={() => patch({ assignee_email: user.email, assignee_name: user.name })}><UserCheck className="h-3.5 w-3.5 mr-1" />Me</Button>
            </div>
            {away && (
              <div className="mt-2 flex flex-wrap items-center gap-2 text-xs border border-amber-300 bg-amber-50 text-amber-800 rounded-sm px-2.5 py-1.5" data-testid="ticket-assignee-away">
                <TreePalm className="h-3.5 w-3.5 shrink-0" />
                <span>{t.assignee_name} is {away.status === "away" ? "out of office" : "scheduled out"} · back {fmtBack(away)}{reroute ? ` · ${reroute.backup_name} is covering` : ""}</span>
                {reroute && <Button size="sm" className="h-7 ml-auto" data-testid="ticket-reroute" onClick={() => { patch({ assignee_name: reroute.backup_name, assignee_email: reroute.backup_email }); toast.success(`Sent to ${reroute.backup_name} instead`); }}><Send className="h-3 w-3 mr-1" /> Send to {reroute.backup_name} instead</Button>}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="mt-4 text-xs text-muted-foreground border-t border-border pt-3">Assigned to <span className="font-medium text-foreground">{t.assignee_name || "—"}</span> · due {t.due_date || "—"}</div>
      )}

      <div className="mt-5 border-t border-border pt-4">
        <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-1"><MessageSquare className="h-3.5 w-3.5" />Activity &amp; Comments</div>
        <div className="space-y-3 max-h-80 overflow-y-auto pr-1" data-testid="ticket-comments">
          {(t.comments || []).map((c) => c.type === "activity" ? (
            <div key={c.id} className="text-[11px] text-muted-foreground italic flex items-center gap-1.5"><Clock className="h-3 w-3 shrink-0" />{c.author_name} {c.body} · {fmt(c.created_at)}</div>
          ) : (
            <div key={c.id} data-testid="ticket-comment" className={`text-sm rounded-sm p-2.5 ${c.internal ? "bg-amber-500/10 border border-amber-500/30" : "bg-secondary/60"}`}>
              <div className="flex items-center gap-2 text-xs text-muted-foreground mb-0.5"><span className="font-semibold text-foreground">{c.author_name}</span>{c.internal && <span className="text-amber-600 font-medium">Internal</span>}<span>· {fmt(c.created_at)}</span></div>
              <div className="whitespace-pre-line text-foreground/90">{c.body}</div>
            </div>
          ))}
        </div>
        {canAgent && <div className="mt-3 flex justify-end"><CannedPicker canManage={["admin", "manager"].includes(user?.role)} onInsert={(b) => setComment((p) => (p ? `${p}\n${b}` : b))} /></div>}
        <div className="mt-2 flex items-start gap-2">
          <Textarea rows={2} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Add a reply…" data-testid="ticket-comment-input" className="flex-1" />
          <Button onClick={addComment} data-testid="ticket-comment-send"><Send className="h-4 w-4" /></Button>
        </div>
        {canAgent && <label className="flex items-center gap-1.5 text-xs text-muted-foreground mt-1.5"><input type="checkbox" checked={internal} onChange={(e) => setInternal(e.target.checked)} data-testid="ticket-internal-toggle" /> Internal note (agents only)</label>}
      </div>
    </div>
  );
}

export default function Tickets() {
  const { user } = useAuth();
  const isManager = ["admin", "manager"].includes(user?.role);
  const isAdmin = user?.role === "admin";
  const location = useLocation();
  const [groups, setGroups] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [active, setActive] = useState(null);
  const [stats, setStats] = useState(null);
  const [scope, setScope] = useState("mine");
  const [filters, setFilters] = useState({ status: "all", priority: "all", group_id: "all" });
  const [q, setQ] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [groupsOpen, setGroupsOpen] = useState(false);
  const [slaOpen, setSlaOpen] = useState(false);
  const [digestOpen, setDigestOpen] = useState(false);
  const [digestId, setDigestId] = useState(null);

  const isAgent = isManager || groups.some((g) => g.is_agent);

  const loadGroups = useCallback(() => api.get("/support-groups").then((r) => setGroups(r.data)).catch(() => {}), []);
  const loadStats = useCallback(() => { api.get("/tickets/stats").then((r) => setStats(r.data)).catch(() => {}); }, []);
  const loadTickets = useCallback(() => {
    const params = { scope };
    if (filters.status !== "all") params.status = filters.status;
    if (filters.priority !== "all") params.priority = filters.priority;
    if (filters.group_id !== "all") params.group_id = filters.group_id;
    if (q) params.q = q;
    api.get("/tickets", { params }).then((r) => setTickets(r.data)).catch(() => {});
  }, [scope, filters, q]);

  const openTicket = useCallback((id) => api.get(`/tickets/${id}`).then((r) => setActive(r.data)).catch(() => toast.error("Could not open ticket")), []);
  const refresh = () => { loadTickets(); loadStats(); if (active) openTicket(active.id); };

  useEffect(() => { loadGroups(); }, [loadGroups]);
  useEffect(() => { loadTickets(); loadStats(); }, [loadTickets, loadStats]);
  useEffect(() => { const p = new URLSearchParams(location.search).get("open"); if (p) openTicket(p); }, [location.search, openTicket]);
  useEffect(() => { const d = new URLSearchParams(location.search).get("digest"); if (d) { setDigestId(d); setDigestOpen(true); } }, [location.search]);

  const SCOPES = [
    { key: "mine", label: "My Tickets", show: true },
    { key: "assigned", label: "Assigned to Me", show: isAgent },
    { key: "queue", label: "Queues", show: isAgent },
    { key: "all", label: "All Tickets", show: isManager },
  ].filter((s) => s.show);

  return (
    <div data-testid="tickets-page">
      <PageHeader overline="Operations" title="Monge Ticketing" subtitle="Submit and track requests across IT, HR, Facilities and Intake.">
        <div className="flex gap-2">
          {isManager && <Button variant="outline" data-testid="ticket-export" onClick={() => window.open(`${BACKEND}/api/tickets/export`, "_blank")}><Download className="h-4 w-4 mr-1.5" />Export</Button>}
          {isManager && <Button variant="outline" data-testid="digest-btn" onClick={() => { setDigestId(null); setDigestOpen(true); }}><CalendarDays className="h-4 w-4 mr-1.5" />Weekly Digest</Button>}
          {isAdmin && <Button variant="outline" data-testid="sla-btn" onClick={() => setSlaOpen(true)}><Gauge className="h-4 w-4 mr-1.5" />SLA</Button>}
          {isAdmin && <Button variant="outline" data-testid="manage-groups-btn" onClick={() => setGroupsOpen(true)}><Settings className="h-4 w-4 mr-1.5" />Manage Groups</Button>}
          <CreateTicketDialog groups={groups} open={createOpen} setOpen={setCreateOpen} onCreated={refresh} />
        </div>
      </PageHeader>

      {isAgent && stats && <StatsStrip stats={stats} />}
      {isManager && <NeedsAttention className="mb-4" />}

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="flex gap-1 border border-border rounded-sm p-0.5" data-testid="ticket-scopes">
          {SCOPES.map((s) => (
            <button key={s.key} data-testid={`scope-${s.key}`} onClick={() => setScope(s.key)}
              className={`text-xs px-3 py-1.5 rounded-sm transition-colors ${scope === s.key ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-accent"}`}>{s.label}</button>
          ))}
        </div>
        <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}><SelectTrigger className="w-36" data-testid="filter-status"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All statuses</SelectItem>{STATUS_ORDER.map((s) => <SelectItem key={s} value={s}>{STATUS[s].label}</SelectItem>)}</SelectContent></Select>
        <Select value={filters.priority} onValueChange={(v) => setFilters({ ...filters, priority: v })}><SelectTrigger className="w-32" data-testid="filter-priority"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All priority</SelectItem>{Object.keys(PRIORITY).map((p) => <SelectItem key={p} value={p}>{PRIORITY[p].label}</SelectItem>)}</SelectContent></Select>
        <Select value={filters.group_id} onValueChange={(v) => setFilters({ ...filters, group_id: v })}><SelectTrigger className="w-40" data-testid="filter-group"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All groups</SelectItem>{groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}</SelectContent></Select>
        <div className="relative flex-1 min-w-[180px]"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search tickets…" className="pl-9" data-testid="ticket-search" /></div>
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-2 space-y-2" data-testid="ticket-list">
          {tickets.length === 0 ? <EmptyState icon={LifeBuoy} title="No tickets here" hint="Create one with New Ticket." /> :
            tickets.map((t) => (
              <button key={t.id} data-testid="ticket-card" onClick={() => openTicket(t.id)}
                className={`w-full text-left border rounded-sm p-3 transition-colors ${active?.id === t.id ? "border-primary bg-primary/5" : "border-border bg-card hover:border-primary/60"}`}>
                <div className="flex items-center gap-2">
                  <span className={`h-2 w-2 rounded-full shrink-0 ${PRIORITY[t.priority]?.dot}`} />
                  <span className="text-[11px] font-mono text-muted-foreground">{t.number}</span>
                  <Chip cls={STATUS[t.status]?.cls}>{STATUS[t.status]?.label}</Chip>
                  <SlaBadge sla={t.sla} />
                  {isOverdue(t) && <span className="text-[10px] text-destructive font-semibold ml-auto">OVERDUE</span>}
                </div>
                <div className="text-sm font-medium mt-1 truncate">{t.subject}</div>
                <div className="text-xs text-muted-foreground truncate mt-0.5">{t.group_name} · {t.assignee_name || "Unassigned"} · {fmt(t.updated_at)}</div>
              </button>
            ))}
        </div>
        <div className="lg:col-span-3">
          {active ? <TicketDetail ticket={active} groups={groups} user={user} onClose={() => setActive(null)} onChange={refresh} /> : (
            <div className="border border-border bg-card rounded-sm p-10 text-center text-muted-foreground" data-testid="ticket-empty-detail">
              <LifeBuoy className="h-8 w-8 mx-auto mb-3 opacity-60" /><p className="text-sm">Select a ticket to view details.</p>
            </div>
          )}
        </div>
      </div>

      {isAdmin && <ManageGroups open={groupsOpen} setOpen={setGroupsOpen} groups={groups} reload={loadGroups} />}
      {isAdmin && <SlaDialog open={slaOpen} setOpen={setSlaOpen} />}
      {isManager && <TicketDigestDialog open={digestOpen} setOpen={setDigestOpen} digestId={digestId} isAdmin={isAdmin} />}
    </div>
  );
}
